/* Pure helpers shared by the documents panel, table and drawers. */
import type { UserProfile } from "../../api/types";
import type { ClientCaseDocument } from "../../api/types/foreignNationalDocument";
import type { ForeignNationalDocument } from "./types";

export type DocumentsSortBy = "" | "created_at" | "filename" | "uploader";
export type DocumentsSortDir = "asc" | "desc";
export type DocumentsSort = { by: DocumentsSortBy; dir: DocumentsSortDir };

export const DEFAULT_DOCUMENTS_SORT: DocumentsSort = { by: "", dir: "desc" };

export const DOCUMENTS_SORT_OPTIONS: Array<{
  key: string;
  label: string;
  sort: DocumentsSort;
}> = [
  { key: "default", label: "Default", sort: { by: "", dir: "desc" } },
  {
    key: "uploaded_newest",
    label: "Uploaded At (Newest)",
    sort: { by: "created_at", dir: "desc" }
  },
  {
    key: "uploaded_oldest",
    label: "Uploaded At (Oldest)",
    sort: { by: "created_at", dir: "asc" }
  },
  {
    key: "filename_az",
    label: "File Name (A-Z)",
    sort: { by: "filename", dir: "asc" }
  },
  {
    key: "uploader_az",
    label: "Upload User (A-Z)",
    sort: { by: "uploader", dir: "asc" }
  }
];

export const isSortSelected = (a: DocumentsSort, b: DocumentsSort): boolean =>
  a.by === b.by && (a.by === "" || a.dir === b.dir);

export const sortLabel = (sort: DocumentsSort): string =>
  DOCUMENTS_SORT_OPTIONS.find((o) => isSortSelected(o.sort, sort))?.label ??
  "Default";

/** Firestore timestamps, ISO strings, epoch numbers and Dates all normalise to ms. */
export const createdAtMs = (
  createdAt: ForeignNationalDocument["created_at"]
): number => {
  if (!createdAt) return 0;
  try {
    const maybe = createdAt as { toDate?: () => Date; seconds?: number };
    if (typeof maybe?.toDate === "function") return maybe.toDate().getTime();
    if (typeof maybe?.seconds === "number") return maybe.seconds * 1000;
    if (typeof createdAt === "string") {
      const t = new Date(createdAt).getTime();
      return Number.isNaN(t) ? 0 : t;
    }
    if (typeof createdAt === "number") return createdAt;
    if (createdAt instanceof Date) return createdAt.getTime();
    return 0;
  } catch {
    return 0;
  }
};

export const formatCreatedAt = (
  createdAt: ForeignNationalDocument["created_at"]
): string => {
  const ms = createdAtMs(createdAt);
  if (!ms) return "—";
  return new Date(ms).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
};

export const uploaderSortKey = (doc: ForeignNationalDocument): string => {
  const name = (doc.created_by_user_name ?? "").trim();
  const email = (doc.created_by_user_email ?? "").trim();
  const id = (doc.created_by_user_id ?? "").trim();
  return (name || email || id || "").toLowerCase();
};

export const docSearchHaystack = (doc: ForeignNationalDocument): string =>
  [
    doc.filename,
    doc.label,
    doc.created_by_user_name,
    doc.created_by_user_email,
    doc.created_by_user_id
  ]
    .map((v) => (typeof v === "string" ? v.trim() : ""))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const filterDocuments = (
  documents: ForeignNationalDocument[],
  query: string
): ForeignNationalDocument[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return documents;
  return documents.filter((d) => docSearchHaystack(d).includes(needle));
};

export const sortDocuments = (
  documents: ForeignNationalDocument[],
  sort: DocumentsSort
): ForeignNationalDocument[] => {
  const by = sort.by || "created_at";
  const dir = sort.by ? sort.dir : "desc";
  const items = [...documents];
  items.sort((a, b) => {
    if (by === "filename") {
      const av = (a.filename ?? "").toLowerCase();
      const bv = (b.filename ?? "").toLowerCase();
      if (av !== bv)
        return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    } else if (by === "uploader") {
      const av = uploaderSortKey(a);
      const bv = uploaderSortKey(b);
      if (av !== bv)
        return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    } else {
      const av = createdAtMs(a.created_at);
      const bv = createdAtMs(b.created_at);
      if (av !== bv) return dir === "asc" ? av - bv : bv - av;
    }
    const an = (a.filename ?? "").toLowerCase();
    const bn = (b.filename ?? "").toLowerCase();
    if (an !== bn) return an.localeCompare(bn);
    return a.foreign_national_document_id.localeCompare(
      b.foreign_national_document_id
    );
  });
  return items;
};

export const formatBytes = (bytes: number): string => {
  const b = Number.isFinite(bytes) ? Math.max(0, bytes) : 0;
  if (b >= 1024 * 1024 * 1024)
    return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${Math.round(b)} B`;
};

export const formatBytesMb = (bytes: number): string => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/** DOM id of a document row, for `?documentId=` deep links. */
export const documentFocusDomId = (id: string): string => `document-${id}`;

export const docIsReady = (doc: ForeignNationalDocument): boolean =>
  Boolean((doc.storage_path ?? "").trim());

/** What the document is, when known; documents are stored as-is and read on demand. */
export const documentLabel = (doc: ForeignNationalDocument): string =>
  (doc.label ?? "").trim();

export const descriptionText = (doc: ForeignNationalDocument): string => {
  if (!doc.storage_path && !doc.download_url) return "Uploading…";
  return "";
};

export const referencedCaseIds = (doc: ForeignNationalDocument): string[] => {
  const usages = Array.isArray(doc.used_in_cases) ? doc.used_in_cases : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of usages) {
    const id = String(u?.case_id ?? "").trim();
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};

export const isPdfDocument = (doc: ForeignNationalDocument | null): boolean => {
  const name = String(doc?.filename ?? "").toLowerCase();
  if (name.endsWith(".pdf")) return true;
  return String(doc?.content_type ?? "").toLowerCase() === "application/pdf";
};

export const isImageDocument = (
  doc: ForeignNationalDocument | null
): boolean => {
  const ct = String(doc?.content_type ?? "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = String(doc?.filename ?? "").toLowerCase();
  return [".png", ".jpg", ".jpeg", ".webp"].some((ext) => name.endsWith(ext));
};

// Uploader identity -----------------------------------------------------------

export type UploaderDisplay = {
  label: string;
  email: string;
  avatarUrl?: string;
  initials: string;
  hasIdentity: boolean;
  /** Multi-line tooltip: name + email, "Loading…" while the profile resolves. */
  tooltip: string;
};

const profileName = (user: UserProfile | null): string =>
  [user?.first_name, user?.last_name]
    .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
    .join(" ")
    .trim();

const initialsFrom = (base: string): string => {
  const parts = base.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2)
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  return `${parts[0]?.[0] ?? "?"}`.toUpperCase();
};

export const uploaderDisplay = (
  doc: ForeignNationalDocument,
  user: UserProfile | null
): UploaderDisplay => {
  const id = (doc.created_by_user_id ?? "").trim();
  const name = (profileName(user) || (doc.created_by_user_name ?? "")).trim();
  const email = ((user?.email ?? doc.created_by_user_email) || "").trim();
  const label = name || email || (id ? `User ${id.slice(0, 6)}` : "Unknown");
  const initials = user
    ? (() => {
        const fromName = `${(user.first_name ?? "").trim()[0] ?? ""}${
          (user.last_name ?? "").trim()[0] ?? ""
        }`.toUpperCase();
        if (fromName) return fromName;
        const e = (user.email ?? "").trim();
        return e ? e[0].toUpperCase() : "?";
      })()
    : initialsFrom(name || email || label);
  const avatarUrl =
    user?.avatar_data_url ??
    ((doc.created_by_user_avatar_url ?? "").trim() || undefined);
  const tooltip = (() => {
    const n = profileName(user);
    if (n || email) return email ? (n ? `${n}\n${email}` : email) : n;
    return id ? "Loading…" : "Unknown";
  })();
  return {
    label,
    email,
    avatarUrl,
    initials,
    hasIdentity: Boolean(name || email),
    tooltip
  };
};

const usDate = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC"
      });
};

/** What was read off a case document, in one line ("H-1B · until May 30, 2026 · EAC…"). */
export const caseDocumentFacts = (doc: ClientCaseDocument): string => {
  const f = doc.facts ?? {};
  const until = f.duration_of_status
    ? "D/S"
    : f.expires
    ? `${doc.kind === "identity" ? "expires" : "until"} ${usDate(f.expires)}`
    : "";
  return [
    f.passport_number,
    f.status,
    until,
    f.receipt_number,
    f.i94_number ? `I-94 ${f.i94_number}` : ""
  ]
    .filter(Boolean)
    .join(" · ");
};
